import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CustomerTenantClient,
  CustomerTenantSearchFilterDto,
  CustomerTenantSearchRequestDto,
  CustomerTenantSearchResultDto,
  OnboardingStatus,
} from "@lib/ShiOneClient";
import { TENANT_ID_UNDEFINED } from "../constants";
import { useApi } from "@utils/useApi";
import { Tenant } from "../types";
import { hasAccountIdentity } from "../accountIdentity";
import ms from "ms";

const QUERY_KEY = "tenant-search";

interface UseGetTenantResult {
  tenants: Tenant[];
  notOnboarded: Tenant[];
  isInitialLoading: boolean;
}

function toOnboardingStatus(
  status: CustomerTenantSearchResultDto["tenantOnboardingStatus"],
): OnboardingStatus | null {
  return status ?? null;
}

export function isOnboardedTenant(status: OnboardingStatus | null): boolean {
  return status === OnboardingStatus.Onboarded;
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value?.trim());
}

export default function useGetTenant(searchString = ""): UseGetTenantResult {
  const api = useApi(CustomerTenantClient);

  const response = useQuery({
    queryKey: [QUERY_KEY, searchString],
    staleTime: ms("60m"),
    queryFn: async () => {
      const request = new CustomerTenantSearchRequestDto({
        page: 0,
        pageSize: 100,
        searchString: searchString || undefined,
        isDescending: false,
        searchFilterInclude: new CustomerTenantSearchFilterDto({
          active: ["true"],
        }),
      });

      return await api.searchCustomerTenants(request);
    },
  });

  const mapped = useMemo(() => {
    const rows = response.data?.items ?? [];
    const tenantById = new Map<string, Tenant>();
    const notOnboardedById = new Map<number, Tenant>();

    const createAccount = (row: CustomerTenantSearchResultDto) => ({
      id: row.autotaskAccountId!,
      name: row.accountName || null,
      axNumber: row.axNumber || null,
      isPrimary:
        row.tenantPrimaryAutotaskAccountId != null &&
        row.tenantPrimaryAutotaskAccountId === row.autotaskAccountId,
      rowKey: row.rowKey || null,
    });

    const addOrUpdateTenant = (
      tenantMap: Map<string | number, Tenant>,
      tenantId: string,
      row: CustomerTenantSearchResultDto,
    ) => {
      if (!tenantMap.has(tenantId)) {
        tenantMap.set(tenantId, {
          id: tenantId,
          name: row.tenantName || null,
          accounts: [createAccount(row)],
          onBoardingStatus: toOnboardingStatus(row.tenantOnboardingStatus),
        });
        return;
      }
      const tenant = tenantMap.get(tenantId);
      if (!tenant) return;
      if (!tenant.accounts.some((acct) => acct.id === row.autotaskAccountId)) {
        tenant.accounts.push(createAccount(row));
      }
    };

    for (const row of rows) {
      if (row.accountIsActive !== true) {
        continue;
      }

      if (
        hasAccountIdentity(
          row.autotaskAccountId,
          row.accountName,
          row.axNumber,
        ) &&
        (row.tenantId == null ||
          !isOnboardedTenant(toOnboardingStatus(row.tenantOnboardingStatus)))
      ) {
        addOrUpdateTenant(
          notOnboardedById as Map<string | number, Tenant>,
          row.tenantId || TENANT_ID_UNDEFINED,
          row,
        );
      }

      if (
        !row.tenantId ||
        !isNonEmptyString(row.tenantName) ||
        !hasAccountIdentity(
          row.autotaskAccountId,
          row.accountName,
          row.axNumber,
        ) ||
        !isOnboardedTenant(toOnboardingStatus(row.tenantOnboardingStatus))
      ) {
        continue;
      }

      addOrUpdateTenant(tenantById, row.tenantId, row);
    }

    return {
      tenants: Array.from(tenantById.values()),
      notOnboarded: Array.from(notOnboardedById.values()),
    };
  }, [response.data?.items]);

  return {
    tenants: mapped.tenants,
    notOnboarded: mapped.notOnboarded,
    isInitialLoading: response.isFetching,
  };
}
